// ─────────────────────────────────────────────────────────
// SV HJÆLP — Flydende AI-medhjælper + fejlindberetning
// Synlig for alle brugere (sagsportal + OS dashboard)
// Sprint: AI-hjælp & fejlindberetning · 2026-04-24
// ─────────────────────────────────────────────────────────
import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
// ── Design tokens (matcher App.js T-objekt) ───────────────
const T = {
 blaa: "#4f46e5",
 turkis: "#0d9488",
 accent: "#06d6a0",
 bg: "#0f172a",
 kort: "rgba(255,255,255,0.04)",
 kant: "1px solid rgba(13,148,136,0.28)",
 r: 14,
 shadow: "0 24px 60px rgba(0,0,0,0.55), 0 4px 16px rgba(0,0,0,0.35)",
};
// ── API-helper (samme mønster som App.js) ─────────────────
async function api(sti, metode, data) {
 const opts = {
 method: metode || "GET",
 credentials: "include",
 headers: {
 "Content-Type": "application/json",
 },
 };
 if (data) opts.body = JSON.stringify(data);
 const res = await fetch("/api/" + sti, opts);
 if (!res.ok) {
 const err = await res.json().catch(() => ({}));
 throw new Error(err.fejl || "Serverfejl");
 }
 return res.json();
}
// Poller AI-køen (GET /api/ai-sag/opgave/:id) hvert 3. sek i op til 3 min og returnerer svarteksten.
// Resultatet gemmes af sv-push-listener som { tekst, genereret }.
async function vent_paa_ai_svar(opgave_id) {
 for (let n = 0; n < 60; n++) {
 await new Promise(r => setTimeout(r, 3000));
 let r;
 try { r = await api("ai-sag/opgave/" + opgave_id); } catch (_) { continue; }
 if (r?.status === "FULDFOERT") {
 const res = r.resultat;
 if (typeof res === "string") { try { const p = JSON.parse(res); return p.tekst || p.svar || res; } catch (_) { return res; } }
 return res?.tekst || res?.svar || "";
 }
 if (r?.status === "FEJLET") throw new Error(r.fejl_besked || "AI-opgave fejlede");
 }
 throw new Error("AI-svaret tog for lang tid");
}
// ── Escape-stack (kopieret fra App.js) ────────────────────
const _esc_stack = [];
function useEscapeClose(isOpen, onClose) {
 useEffect(() => {
 if (!isOpen || !onClose) return;
 const entry = { onClose };
 _esc_stack.push(entry);
 const handler = (e) => {
 if (e.key === "Escape") {
 const top = _esc_stack[_esc_stack.length - 1];
 if (top === entry) { e.preventDefault(); onClose(); }
 }
 };
 window.addEventListener("keydown", handler);
 return () => {
 window.removeEventListener("keydown", handler);
 const i = _esc_stack.lastIndexOf(entry);
 if (i !== -1) _esc_stack.splice(i, 1);
 };
 }, [isOpen, onClose]);
}
// ── ChatBesked ────────────────────────────────────────────
function ChatBesked({ role, content }) {
 const er_bruger = role === "user";
 return (
 <div style={{
 display: "flex",
 justifyContent: er_bruger ? "flex-end" : "flex-start",
 marginBottom: 10,
 }}>
 {!er_bruger && (
 <div style={{
 width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
 background: "linear-gradient(135deg,#0d9488,#4f46e5)",
 display: "flex", alignItems: "center", justifyContent: "center",
 fontSize: 14, marginRight: 8, alignSelf: "flex-end", marginBottom: 2,
 }}></div>
 )}
 <div style={{
 maxWidth: "80%",
 background: er_bruger ? "rgba(79,70,229,0.25)" : "rgba(13,148,136,0.15)",
 border: er_bruger ? "1px solid rgba(79,70,229,0.4)" : "1px solid rgba(13,148,136,0.3)",
 borderRadius: er_bruger ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
 padding: "10px 14px",
 fontSize: 13.5,
 color: "#e8eaf6",
 lineHeight: 1.55,
 whiteSpace: "pre-wrap",
 wordBreak: "break-word",
 }}>
 {content}
 </div> </div>
 );
}
// ── HjaelpFane — AI-chat ──────────────────────────────────
const VELKOMST = { role: "assistant", content: "Hej! Jeg er din AI-medhjælper i sagsportalen. Hvad kan jeg hjælpe dig med? \n\nJeg kender alle portalens funktioner og kan guide dig trin for trin." };
const CHAT_KEY = "sv_hjaelp_chat";
const CHAT_MAX = 60;
const CHAT_MAX_ALDER_DAGE = 14;
function gem_chat(beskeder) {
 try {
 localStorage.setItem(CHAT_KEY, JSON.stringify({ beskeder, tid: Date.now() }));
 } catch (_) {}
}
function hent_chat() {
 try {
 const raw = localStorage.getItem(CHAT_KEY);
 if (!raw) return null;
 const { beskeder, tid } = JSON.parse(raw);
 if (!beskeder || Date.now() - tid > CHAT_MAX_ALDER_DAGE * 86400000) return null;
 return beskeder;
 } catch (_) { return null; }
}
function HjaelpFane() {
 const [beskeder, setBeskeder] = useState(() => hent_chat() || [VELKOMST]);
 const [input, setInput] = useState("");
 const [sender, setSender] = useState(false);
 const bund_ref = useRef(null);
 const input_ref = useRef(null);
 useEffect(() => {
 gem_chat(beskeder.slice(-CHAT_MAX));
 }, [beskeder]);
 useEffect(() => {
 window.__sv_hjaelp_ryd = () => setBeskeder([VELKOMST]);
 return () => { delete window.__sv_hjaelp_ryd; };
 }, []);
 useEffect(() => {
 bund_ref.current?.scrollIntoView({ behavior: "smooth" });
 }, [beskeder]);
 const send = useCallback(async () => {
 const tekst = input.trim();
 if (!tekst || sender) return;
 const ny_bruger = { role: "user", content: tekst };
 const opdateret = [...beskeder, ny_bruger];
 setBeskeder(opdateret);
 setInput("");
 setSender(true);
 try {
 const til_api = opdateret.slice(1).slice(-12);
 const d = await api("hjaelp/ai-chat", "POST", { beskeder: til_api });
 // Backend svarer asynkront via AI-køen ({ opgave_id, status:'afventer' }) — poll resultatet
 // som de øvrige AI-funktioner gør (GET /api/ai-sag/opgave/:id). d.svar bevares som direkte vej.
 let svar = d?.svar || "";
 if (!svar && d?.opgave_id) svar = await vent_paa_ai_svar(d.opgave_id);
 setBeskeder(p => [...p, { role: "assistant", content: svar || "Beklager, jeg fik intet svar. Prøv igen." }]);
 } catch (err) {
 console.error('[SvHjaelp] Chat fejl:', err);
 setBeskeder(p => [...p, {
 role: "assistant",
 content: "Beklager, jeg kunne ikke svare. Prøv igen om lidt.",
 }]);
 } finally {
 setSender(false);
 setTimeout(() => input_ref.current?.focus(), 50);
 }
 }, [input, sender, beskeder]);
 const tastatur = (e) => {
 if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
 };
 return (
 <div style={{ display: "flex", flexDirection: "column", height: "100%" }}> <div style={{
 flex: 1, overflowY: "auto", padding: "16px 14px",
 scrollbarWidth: "thin",
 scrollbarColor: "rgba(255,255,255,0.1) transparent",
 }}>
 {beskeder.map((b, i) => <ChatBesked key={i} role={b.role} content={b.content} />)}
 {sender && (
 <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#64748b", fontSize: 12.5, padding: "4px 8px" }}> <div style={{ display: "flex", gap: 3 }}>
 {[0, 1, 2].map(i => (
 <div key={i} style={{
 width: 5, height: 5, borderRadius: "50%", background: T.accent,
 animation: `bounce 1.2s ${i * 0.2}s ease-in-out infinite`,
 }} />
 ))}
 </div>
 Tænker…
 </div>
 )}
 <div ref={bund_ref} /> </div> <div style={{
 borderTop: "1px solid rgba(255,255,255,0.08)",
 padding: "12px 14px",
 display: "flex", gap: 8, alignItems: "flex-end",
 }}> <textarea
 ref={input_ref}
 value={input}
 onChange={e => setInput(e.target.value)}
 onKeyDown={tastatur}
 placeholder="Stil et spørgsmål… (Enter sender)"
 disabled={sender}
 rows={1}
 style={{
 flex: 1, resize: "none", background: "rgba(255,255,255,0.05)",
 border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10,
 color: "#e8eaf6", fontSize: 13.5, padding: "9px 12px",
 outline: "none", fontFamily: "inherit", lineHeight: 1.5,
 minHeight: 38, maxHeight: 100, overflowY: "auto",
 transition: "border-color 0.2s",
 }}
 onFocus={e => e.target.style.borderColor = "rgba(13,148,136,0.5)"}
 onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.12)"}
 /> <button
 onClick={send}
 disabled={!input.trim() || sender}
 style={{
 width: 38, height: 38, borderRadius: 10, border: "none", cursor: "pointer",
 background: !input.trim() || sender ? "rgba(255,255,255,0.07)" : "linear-gradient(135deg,#0d9488,#4f46e5)",
 color: !input.trim() || sender ? "#64748b" : "#fff",
 fontSize: 16, flexShrink: 0, transition: "all 0.2s",
 display: "flex", alignItems: "center", justifyContent: "center",
 }}
 title="Send (Enter)"
 >
 ↑
 </button> </div> </div>
 );
}
// ── FejlFane — Fejlindberetning ───────────────────────────
function FejlFane() {
 const [beskrivelse, setBeskrivelse] = useState("");
 const [screenshot, setScreenshot] = useState(null);
 const [kritisk, setKritisk] = useState(false);
 const [sender, setSender] = useState(false);
 const [sendt, setSendt] = useState(false);
 const [fejl, setFejl] = useState(null);
 const [drag_over, setDragOver] = useState(false);
 const fil_ref = useRef(null);
 const container_ref = useRef(null);
 const læs_fil = useCallback((fil) => {
 if (!fil) return;
 if (!fil.type.startsWith("image/")) {
 setFejl("Kun billedfiler (PNG, JPG) understøttes");
 return;
 }
 if (fil.size > 5 * 1024 * 1024) {
 setFejl("Screenshot er for stort (maks. 5 MB)");
 return;
 }
 const reader = new FileReader();
 reader.onload = (e) => { setScreenshot(e.target.result); setFejl(null); };
 reader.readAsDataURL(fil);
 }, []);
 useEffect(() => {
 const handler = (e) => {
 const items = e.clipboardData?.items;
 if (!items) return;
 for (const item of items) {
 if (item.kind === "file" && item.type.startsWith("image/")) {
 e.preventDefault();
 læs_fil(item.getAsFile());
 return;
 }
 }
 };
 document.addEventListener("paste", handler);
 return () => document.removeEventListener("paste", handler);
 }, [læs_fil]);
 useEffect(() => {
 const el = container_ref.current;
 if (!el) return;
 let drag_counter = 0;
 const on_enter = (e) => { e.preventDefault(); drag_counter++; setDragOver(true); };
 const on_over = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; };
 const on_leave = (e) => { drag_counter--; if (drag_counter <= 0) { drag_counter = 0; setDragOver(false); } };
 const on_drop = (e) => {
 e.preventDefault();
 drag_counter = 0;
 setDragOver(false);
 const fil = e.dataTransfer?.files?.[0];
 if (fil) læs_fil(fil);
 };
 el.addEventListener("dragenter", on_enter);
 el.addEventListener("dragover", on_over);
 el.addEventListener("dragleave", on_leave);
 el.addEventListener("drop", on_drop);
 return () => {
 el.removeEventListener("dragenter", on_enter);
 el.removeEventListener("dragover", on_over);
 el.removeEventListener("dragleave", on_leave);
 el.removeEventListener("drop", on_drop);
 };
 }, [læs_fil]);
 const send = async () => {
 if (!beskrivelse.trim()) { setFejl("Beskriv venligst fejlen"); return; }
 setSender(true);
 setFejl(null);
 try {
 await api("hjaelp-fejl", "POST", {
 beskrivelse: beskrivelse.trim(),
 screenshot: screenshot || undefined,
 kritisk,
 });
 setSendt(true);
 } catch (err) {
 console.error('[SvHjaelp] Fejlrapport send fejl:', err);
 setFejl("Fejlrapport kunne ikke sendes — prøv igen");
 } finally {
 setSender(false);
 }
 };
 if (sendt) {
 return (
 <div style={{
 flex: 1, display: "flex", flexDirection: "column",
 alignItems: "center", justifyContent: "center", padding: 32, textAlign: "center",
 }}> <div style={{ fontSize: 40, marginBottom: 16 }}></div> <div style={{ fontSize: 16, fontWeight: 700, color: T.accent, marginBottom: 8 }}>
 Fejl indberettet!
 </div> <div style={{ fontSize: 13, color: "#94a3b8", lineHeight: 1.6, marginBottom: 24 }}>
 {kritisk
 ? "Din fejlindberetning er markeret som kritisk og vises øverst i OS-dashboardet."
 : "Din fejlindberetning er sendt til OS-dashboardet og vil blive behandlet snarest."}
 </div> <button
 onClick={() => { setSendt(false); setBeskrivelse(""); setScreenshot(null); setKritisk(false); }}
 style={{
 padding: "8px 20px", borderRadius: 10, border: T.kant, cursor: "pointer",
 background: "rgba(13,148,136,0.12)", color: T.accent, fontSize: 13, fontWeight: 600,
 }}
 >
 Indmeld ny fejl
 </button> </div>
 );
 }
 return (
 <div
 ref={container_ref}
 style={{
 flex: 1, overflowY: "auto", padding: "16px 14px",
 display: "flex", flexDirection: "column", gap: 14,
 outline: drag_over ? `2px solid ${T.accent}` : "2px solid transparent",
 outlineOffset: -2,
 borderRadius: 4,
 transition: "outline-color 0.15s",
 background: drag_over ? "rgba(13,148,136,0.04)" : "transparent",
 position: "relative",
 }}
 >
 {drag_over && (
 <div style={{
 position: "absolute", inset: 0, zIndex: 10, borderRadius: 4,
 display: "flex", alignItems: "center", justifyContent: "center",
 pointerEvents: "none",
 }}> <div style={{
 background: "rgba(13,25,40,0.88)", border: `1.5px solid ${T.accent}`,
 borderRadius: 12, padding: "14px 24px", fontSize: 14, fontWeight: 600,
 color: T.accent, display: "flex", alignItems: "center", gap: 10,
 }}>
 Slip for at vedhæfte screenshot
 </div> </div>
 )}
 <div> <label style={{ fontSize: 12.5, color: "#94a3b8", fontWeight: 600, display: "block", marginBottom: 6 }}>
 Beskriv fejlen *
 </label> <textarea
 value={beskrivelse}
 onChange={e => setBeskrivelse(e.target.value)}
 placeholder="Hvad skete der? Hvad forsøgte du at gøre? Beskriv gerne trin for trin…"
 rows={4}
 style={{
 width: "100%", resize: "vertical",
 background: "rgba(255,255,255,0.05)",
 border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10,
 color: "#e8eaf6", fontSize: 13, padding: "10px 12px",
 outline: "none", fontFamily: "inherit", lineHeight: 1.55,
 boxSizing: "border-box", minHeight: 90,
 transition: "border-color 0.2s",
 }}
 onFocus={e => e.target.style.borderColor = "rgba(13,148,136,0.5)"}
 onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.12)"}
 /> <div style={{ textAlign: "right", fontSize: 11, color: beskrivelse.length > 4500 ? "#f97316" : "#64748b", marginTop: 3 }}>
 {beskrivelse.length}/5000
 </div> </div> <div> <label style={{ fontSize: 12.5, color: "#94a3b8", fontWeight: 600, display: "block", marginBottom: 6 }}>
 Screenshot (valgfrit)
 </label>
 {screenshot ? (
 <div style={{ position: "relative" }}> <img
 src={screenshot}
 alt="Screenshot forhåndsvisning"
 style={{ width: "100%", borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)", maxHeight: 180, objectFit: "contain", background: "#000" }}
 /> <button
 onClick={() => setScreenshot(null)}
 style={{
 position: "absolute", top: 6, right: 6,
 background: "rgba(0,0,0,0.7)", border: "none", borderRadius: "50%",
 width: 24, height: 24, cursor: "pointer", color: "#fff", fontSize: 12,
 display: "flex", alignItems: "center", justifyContent: "center",
 }}
 title="Fjern screenshot"
 ></button> </div>
 ) : (
 <div
 onClick={() => fil_ref.current?.click()}
 style={{
 border: "1.5px dashed rgba(255,255,255,0.12)", borderRadius: 10,
 padding: "18px 16px", textAlign: "center", cursor: "pointer",
 color: "#64748b", fontSize: 12.5, lineHeight: 1.7,
 transition: "border-color 0.2s, background 0.2s",
 }}
 onMouseOver={e => { e.currentTarget.style.borderColor = "rgba(13,148,136,0.4)"; e.currentTarget.style.background = "rgba(13,148,136,0.04)"; }}
 onMouseOut={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)"; e.currentTarget.style.background = "transparent"; }}
 >
 Klik for at vælge · træk-og-slip · eller Ctrl+V<br /> <span style={{ fontSize: 11, color: "#475569" }}>PNG, JPG · maks. 5 MB</span> </div>
 )}
 <input ref={fil_ref} type="file" accept="image/*" style={{ display: "none" }} onChange={e => { if (e.target.files?.[0]) læs_fil(e.target.files[0]); }} /> </div> <label style={{
 display: "flex", alignItems: "center", gap: 10, cursor: "pointer",
 padding: "10px 12px", borderRadius: 10,
 background: kritisk ? "rgba(239,68,68,0.08)" : "rgba(255,255,255,0.03)",
 border: kritisk ? "1px solid rgba(239,68,68,0.3)" : "1px solid rgba(255,255,255,0.08)",
 transition: "all 0.2s",
 }}> <input
 type="checkbox"
 checked={kritisk}
 onChange={e => setKritisk(e.target.checked)}
 style={{ width: 16, height: 16, accentColor: "#ef4444", cursor: "pointer" }}
 /> <div> <div style={{ fontSize: 13, fontWeight: 600, color: kritisk ? "#f87171" : "#94a3b8" }}>
 Kritisk fejl
 </div> <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 1 }}>
 Markér hvis fejlen blokerer for arbejdet — vises øverst i OS-dashboardet
 </div> </div> </label>
 {fejl && (
 <div style={{
 background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)",
 borderRadius: 8, padding: "8px 12px", fontSize: 12.5, color: "#fca5a5",
 }}>
 {fejl}
 </div>
 )}
 <button
 onClick={send}
 disabled={sender || !beskrivelse.trim()}
 style={{
 padding: "11px 0", borderRadius: 10, border: "none", cursor: sender || !beskrivelse.trim() ? "not-allowed" : "pointer",
 background: sender || !beskrivelse.trim()
 ? "rgba(255,255,255,0.07)"
 : kritisk
 ? "linear-gradient(135deg,#dc2626,#b91c1c)"
 : "linear-gradient(135deg,#0d9488,#4f46e5)",
 color: sender || !beskrivelse.trim() ? "#64748b" : "#fff",
 fontSize: 14, fontWeight: 700, marginTop: 4,
 transition: "all 0.2s",
 }}
 >
 {sender ? "Sender…" : kritisk ? " Indsend kritisk fejl" : "Indsend fejlrapport"}
 </button> </div>
 );
}
// ── Hoved-komponent ───────────────────────────────────────
export default function SvHjaelp({ bruger, kontekst = "Sagsportal" }) {
 const [åben, setÅben] = useState(false);
 const [fane, setFane] = useState("hjaelp");
 useEscapeClose(åben, () => setÅben(false));
 const [pos, setPos] = useState(() => {
 try { const s = localStorage.getItem('sv_hjaelp_pos'); return s ? JSON.parse(s) : null; }
 catch(_) { return null; }
 });
 const dragRef = useRef({ active: false, startX: 0, startY: 0, origX: 0, origY: 0, moved: false });
 const buttonRef = useRef(null);
 const onDragStart = useCallback((e) => {
 const r = dragRef.current;
 r.active = true; r.moved = false;
 r.startX = e.clientX; r.startY = e.clientY;
 r.origX = pos ? pos.x : window.innerWidth - 76;
 r.origY = pos ? pos.y : window.innerHeight - 76;
 function onMove(ev) {
 const dx = ev.clientX - r.startX, dy = ev.clientY - r.startY;
 if (Math.abs(dx) > 4 || Math.abs(dy) > 4) r.moved = true;
 const nx = Math.max(0, Math.min(window.innerWidth - 52, r.origX + dx));
 const ny = Math.max(0, Math.min(window.innerHeight - 52, r.origY + dy));
 if (buttonRef.current) {
 buttonRef.current.style.left = nx + 'px';
 buttonRef.current.style.top = ny + 'px';
 buttonRef.current.style.right = 'auto';
 buttonRef.current.style.bottom = 'auto';
 }
 }
 function onUp(ev) {
 r.active = false;
 document.removeEventListener('mousemove', onMove);
 document.removeEventListener('mouseup', onUp);
 if (r.moved) {
 const dx = ev.clientX - r.startX, dy = ev.clientY - r.startY;
 const nx = Math.max(0, Math.min(window.innerWidth - 52, r.origX + dx));
 const ny = Math.max(0, Math.min(window.innerHeight - 52, r.origY + dy));
 const np = { x: nx, y: ny };
 localStorage.setItem('sv_hjaelp_pos', JSON.stringify(np));
 setPos(np);
 }
 }
 document.addEventListener('mousemove', onMove);
 document.addEventListener('mouseup', onUp);
 }, [pos]);
 if (!bruger) return null;
 const knap = (
 <button
 ref={buttonRef}
 className="sv-ai-fab"
 onClick={(e) => { if (!dragRef.current.moved) setÅben(p => !p); dragRef.current.moved = false; }}
 onMouseDown={onDragStart}
 title="AI-hjælp & fejlindberetning"
 aria-label="Åbn AI-hjælp og fejlindberetning"
 style={{
 position: "fixed",
 bottom: pos ? "auto" : 24,
 right: pos ? "auto" : 24,
 top: pos ? pos.y : "auto",
 left: pos ? pos.x : "auto",
 zIndex: 8900,
 width: 52,
 height: 52,
 borderRadius: "50%",
 border: "none",
 cursor: "grab",
 background: åben
 ? "linear-gradient(135deg,#0f766e,#0d9488)"
 : "linear-gradient(135deg,#0d9488,#4f46e5)",
 color: "#fff",
 fontSize: 22,
 boxShadow: åben
 ? "0 4px 20px rgba(13,148,136,0.5)"
 : "0 4px 20px rgba(13,148,136,0.5), 0 2px 8px rgba(0,0,0,0.4)",
 transition: "all 0.25s cubic-bezier(0.34,1.56,0.64,1)",
 transform: åben ? "rotate(45deg) scale(0.9)" : "scale(1)",
 display: "flex",
 alignItems: "center",
 justifyContent: "center",
 userSelect: "none",
 }}
 onMouseOver={e => { if (!åben) e.currentTarget.style.transform = "scale(1.1)"; }}
 onMouseOut={e => { if (!åben) e.currentTarget.style.transform = "scale(1)"; }}
 >
 {åben ? "" : ""}
 </button>
 );
 const modal = åben && (
 <div
 onClick={e => { if (e.target === e.currentTarget) setÅben(false); }}
 style={{
 position: "fixed", inset: 0, zIndex: 8800,
 background: "rgba(0,0,0,0.35)",
 display: "flex", alignItems: "flex-end", justifyContent: "flex-end",
 padding: "0 24px 88px 24px",
 }}
 > <div
 className="sv-ai-hjaelp-panel"
 onClick={e => e.stopPropagation()}
 style={{
 position: "relative",
 width: "min(420px, calc(100vw - 48px))",
 height: "min(560px, calc(100vh - 120px))",
 background: "#0f172a",
 border: "1px solid rgba(13,148,136,0.3)",
 borderRadius: 18,
 boxShadow: T.shadow,
 display: "flex",
 flexDirection: "column",
 overflow: "hidden",
 animation: "slideUp 0.25s cubic-bezier(0.34,1.56,0.64,1)",
 }}
 >
 {/* Accent strip */}
 <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:"linear-gradient(90deg,#0d9488,#4f46e5,transparent)", borderRadius:"18px 18px 0 0", pointerEvents:"none", zIndex:1 }} />
 {/* Header */}
 <div style={{
 padding: "14px 18px 0",
 borderBottom: "1px solid rgba(255,255,255,0.06)",
 flexShrink: 0,
 }}> <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}> <div> <div style={{ fontSize: 14, fontWeight: 700, color: T.accent, letterSpacing: "-0.01em" }}>
 AI-medhjælper
 </div> <div style={{ fontSize: 11, color: "#64748b", marginTop: 1 }}>
 {kontekst} · {bruger.navn}
 </div> </div>
 {fane === "hjaelp" && (
 <button
 onClick={() => {
 localStorage.removeItem(CHAT_KEY);
 window.__sv_hjaelp_ryd && window.__sv_hjaelp_ryd();
 }}
 title="Ryd chathistorik og start forfra"
 style={{
 background: "none", border: "none", color: "#475569", cursor: "pointer",
 fontSize: 11, padding: "2px 6px", borderRadius: 5, lineHeight: 1,
 transition: "color 0.15s",
 }}
 onMouseOver={e => e.currentTarget.style.color = "#94a3b8"}
 onMouseOut={e => e.currentTarget.style.color = "#475569"}
 >↺ Ryd</button>
 )}
 <button
 onClick={() => setÅben(false)}
 style={{
 background: "none", border: "none", color: "#64748b", cursor: "pointer",
 fontSize: 18, padding: "2px 6px", borderRadius: 6, lineHeight: 1,
 transition: "color 0.15s",
 }}
 onMouseOver={e => e.currentTarget.style.color = "#e8eaf6"}
 onMouseOut={e => e.currentTarget.style.color = "#64748b"}
 aria-label="Luk"
 ></button> </div>
 {/* Fane-navigation */}
 <div style={{ display: "flex", gap: 2 }}>
 {[
 { id: "hjaelp", label: " Hjælp", title: "Stil spørgsmål til AI-medhjælperen" },
 { id: "fejl", label: " Indmeld fejl", title: "Beskriv en fejl i portalen" },
 ].map(f => (
 <button
 key={f.id}
 onClick={() => setFane(f.id)}
 title={f.title}
 style={{
 padding: "7px 16px", background: "none", border: "none",
 cursor: "pointer", fontSize: 13, fontWeight: fane === f.id ? 700 : 400,
 color: fane === f.id ? T.accent : "#64748b",
 borderBottom: fane === f.id ? `2px solid ${T.accent}` : "2px solid transparent",
 marginBottom: -1, transition: "all 0.15s",
 }}
 >
 {f.label}
 </button>
 ))}
 </div> </div>
 {/* Fane-indhold */}
 <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
 {fane === "hjaelp" && <HjaelpFane />}
 {fane === "fejl" && <FejlFane />}
 </div> </div> </div>
 );
 return createPortal(
 <> <style>{`
 @keyframes slideUp {
 from { opacity: 0; transform: translateY(20px) scale(0.97); }
 to { opacity: 1; transform: translateY(0) scale(1); }
 }
 @keyframes bounce {
 0%, 80%, 100% { transform: translateY(0); opacity: 0.5; }
 40% { transform: translateY(-5px); opacity: 1; }
 }
 `}</style>
 {knap}
 {modal}
 </>,
 document.body
 );
}